/**
 * Masthead observer firing one post-add notification per multi-add device that
 * enters a "needs attention" chip bucket. Wizard/single-add devices never
 * appear because only `runBulkCommit` populates the origin slice.
 *
 * Declarative reconciliation: the desired alert is a pure function of the
 * device's current missing set (0 → none, 1 → specific, ≥2 → combined), keyed
 * by toast id. Each tick compares desired vs last-posted identity: equal → no-op
 * (respects dismissal), changed → clear old + post new, none → auto-clear. This
 * makes stale toasts and per-device stacking impossible by construction.
 *
 * Tracking lives in a depot slice, not a `useRef`, so it survives the masthead
 * unmount/remount on navigation and never re-posts a dismissed alert.
 *
 * Per rule think-in-systems: pure computation lives in the resolver; the cascade
 * (`deriveChipState`/`deriveMissingBuckets`) is the sole chip-verdict source.
 * Per rule reference-rules-in-comments.
 */
import { createElement, useContext, useEffect } from 'react';
import type React from 'react';
import type { Agent } from '@studio/models/agent';
import type { Device, Model } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { locateProxy } from '#~/agent/utils.js';
import { depot } from '#~/depot/depot.js';
import { useDepot } from '#~/depot/hooks.js';
import {
  NO_CONNECTION_HOLD,
  isAspectResolved,
  isConnectionHoldOrphaned,
  isVideoAspectResolved,
  noteConnectionObservation,
  releaseConnectionHold
} from '#~/room/components/video/sources/item/connection-failure-hold.js';
import {
  deriveChipState,
  deriveMissingBuckets
} from '#~/room/components/video/sources/item/chip-state.js';
// Transitory-placeholder store (ENG-4486 Fix A.2). Auth-required bulk-add
// cameras live here — NOT in depot `room.devices` (device/transitory.ts) —
// so the observer lookup must fall back to this store to fire their
// "Authenticate" toast.
import {
  getTransitoryDevice,
  toSyntheticDevice,
  useTransitoryDevices
} from '#~/device/transitory.js';
import { clear, hide, post } from '#~/notifications/index.js';
// Per-device last-desired-alert tracking for the declarative reconciliation.
import { clearLastDesiredAlert, setLastDesiredAlert } from '#~/notifications/multi-add-origins.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { emitMultiAddAnalytics } from '#~/analytics/utils/emit-multi-add-analytics.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
// Per rule stay-in-scope: reuse annotation.tsx's chip-click deep-link via
// `useSettings`; the route-state pattern has no consumer here and no-ops.
import { useSettings } from '#~/room/components/video/hooks.js';
// Read the Room context directly (not `useRoom`, which throws off-Room) because
// `<Notifications>` mounts above the Room scaffold on every protected route.
import { Context as RoomContext } from '#~/room/provider/context.js';
import { Panel } from '#~/video/components/settings/store/index.js';
import { skipReasonToDefaultTab } from '#~/video/components/settings/iris-connection/skip-reason-to-tab.js';
import {
  buildBucketFireToastPayload,
  evaluateDesiredFires,
  fireToChipType,
  type BucketFire,
  type LookupOriginChipFn
} from './multi-add-bucket-resolver.js';

// Re-export the pure-function seams + types so the existing import
// surface from `notifications/hooks/use-multi-add-bucket-notifications`
// remains stable (tests import resolver symbols directly from
// `./multi-add-bucket-resolver.js`).
export {
  bucketToToastCopy,
  buildBucketFireToastPayload,
  chipTargetTabToBucket,
  evaluateDesiredFires
} from './multi-add-bucket-resolver.js';
export type {
  BucketFire,
  BucketFireToastPayload,
  LookupOriginChipFn,
  OriginDeviceSnapshot
} from './multi-add-bucket-resolver.js';

/**
 * Which room a transitory placeholder's settings dialog opens against.
 *
 * A placeholder is in no `room.devices`, so there is nothing to walk: its room
 * is the room the user is in, and the depot `rooms` slice is that answer —
 * `room/provider/provider.tsx` writes an entry on mount and deletes it on
 * unmount, one scaffold at a time. Reading the id back out of the slice is
 * also the guard: an id absent from `rooms` is what makes `Slicer.room()`
 * `ensureNotNil`-throw inside `<SettingsWrapper>`.
 *
 * `undefined` (no room, or several with nothing to disambiguate them) means
 * the caller must no-op — opening against a room that is not in state throws.
 *
 * Per `think-in-systems` (ai.knowledge/global): pure, so the hook stays the
 * thin React adapter.
 *
 * @param rooms - The depot `rooms` slice (mounted rooms only).
 * @param contextRoomId - `useContext(RoomContext)`; defined only inside a room
 *   provider, which `router.tsx` never is.
 * @returns The room id to open against, or `undefined` when unresolvable.
 */
export const resolveMountedRoomId = (
  rooms: Record<Room['id'], unknown>,
  contextRoomId: T_Maybe<Room['id']>
): T_Maybe<Room['id']> => {
  // Exact when present — prefer it over the slice scan.
  if (contextRoomId !== undefined && rooms[contextRoomId] !== undefined) {
    return contextRoomId;
  }

  const mounted = Object.keys(rooms);

  return mounted.length === 1 ? (mounted[0] as Room['id']) : undefined;
};

/**
 * `useMultiAddBucketNotifications` — main hook entry. Mounts the
 * observer side-effect on the masthead so post-add notifications fire
 * for any multi-add-committed device that enters a "needs attention"
 * chip bucket.
 *
 * Returns `void` — the hook owns no rendered state. The notification it
 * emits via `post()` lands in the general `notifications` slice, which
 * drives the tray list item (the surviving Multi-Add surface) and the
 * bell's unread-count badge.
 *
 * @param overrides - Test seams. Production caller passes nothing.
 */
export const useMultiAddBucketNotifications = (overrides?: {
  lookupChipState?: LookupOriginChipFn;
  // Test-only seam for the post + analytics emit; reconciliation runs regardless.
  dispatchFire?: (fire: BucketFire) => void;
}): void => {
  // `useSettings()` returns a callback that opens the Advanced Settings
  // dialog for a Device at a specific sub-tab. Same hook the per-tile
  // chip uses in `annotation.tsx` — captured here at hook scope so the
  // `dispatchFire` default can fire it from the toast action click.
  //
  // Crash guard for non-Room routes: `<Notifications>` mounts in
  // `router.tsx`'s protected-route wrapper (line ~75), which renders
  // on EVERY protected route — `/`, `/agent/setup/*`, `/agent/link/*`,
  // `/buildInfo`, etc. — not just inside the Room scaffold. When
  // outside a Room, calling `useSettings()` with no roomId triggers
  // its internal `roomId ?? useRoom(...)` fallback; `useRoom` does
  // `ensureNotNil(useContext(RoomContext))` which throws
  // "Unexpected T_Nil" because the protected-route wrapper sits ABOVE
  // any `<Context.Provider>`. The crash bricks the lobby.
  //
  // Direct `useContext(RoomContext)` does NOT throw on missing
  // provider (returns `undefined`). Reading it ourselves lets us pass
  // a non-null roomId to `useSettings`, bypassing the internal
  // `useRoom` fallback entirely.
  //
  // That argument is an INERT PLACEHOLDER, never the room we open
  // against: `<Notifications>` is a SIBLING of the routed `<Outlet>`
  // (`routes/router.tsx`), so this read is `undefined` on EVERY route,
  // room routes included. Shipping the sentinel to the dialog would
  // `ensureNotNil`-throw in `Slicer.room()` and hand a bogus id to auth
  // submission, so `openSettingsForFire` resolves the real room at CLICK
  // time and passes it per call via `options.roomId`. The sentinel only
  // keeps `useSettings` off its conditional `useRoom` path.
  //
  // Per `verify-and-escalate` (ai.knowledge/global): the crash was
  // surfaced by independent review after the chip-pattern refactor —
  // pattern transplanted to a host without the Room context the chip's
  // call site (`<Annotation>` inside `<Room>`) takes for granted. This
  // guard documents the host difference.
  //
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const currentRoomId = useContext(RoomContext);
  // Cast: `Room['id']` is a branded string type; the sentinel never
  // reaches `dialog.open` — every call below carries an explicit
  // `options.roomId`. The cast is local and dead-path-only.
  const SETTINGS_FALLBACK_ROOM_ID = 'outside-room-sentinel' as Room['id'];
  const openSettings = useSettings(currentRoomId ?? SETTINGS_FALLBACK_ROOM_ID);
  // Origin slice: a fresh bulk-commit add or auth-commit migration re-runs the effect.
  const origins = useDepot((µ) => µ.multiAddOriginByDeviceId);
  // Heartbeat: agents change on fresh state-sync snapshots, re-running the chip cascade.
  const agents = useDepot((µ) => µ.agents);
  // Rooms supply the Device record (agentStatus, modelId) the chip cascade reads.
  const rooms = useDepot((µ) => µ.rooms);
  // Transitory placeholders live outside the depot; the lookup falls back to them.
  const transitoryDevices = useTransitoryDevices();
  // Last-posted desired-alert identity per device — the reconciliation state.
  const lastDesired = useDepot((µ) => µ.lastDesiredAlertByDevice);
  // Hold slice: re-reconcile with the chip so toast and chip clear together.
  const connectionHolds = useDepot((µ) => µ.connectionFailureHoldByDevice);

  useEffect(() => {
    // Build the lookup inside the effect so it closes over the latest rooms snapshot.
    const lookupChipState: LookupOriginChipFn =
      overrides?.lookupChipState ??
      ((deviceId) => {
        // Origin slice carries no room id, so walk every room for this device.
        let deviceRecord:
          | {
              agentStatus?: T_Maybe<Agent['status']>;
              modelId?: T_Maybe<Model['id']>;
              name?: string;
            }
          | undefined;
        for (const room of Object.values(rooms)) {
          // `room.devices` is keyed by the same Device ids the origin slice uses.
          const candidate = (
            room.devices as Record<
              string,
              {
                agentStatus?: T_Maybe<Agent['status']>;
                modelId?: T_Maybe<Model['id']>;
                name?: string;
              }
            >
          )[deviceId];
          if (candidate) {
            deviceRecord = candidate;
            break;
          }
        }
        if (!deviceRecord) {
          // Not in any room: may be a transitory placeholder (auth-required
          // bulk-add camera). It is unconditionally auth-only, so fire its
          // "Authenticate" toast; the origin migrates to the real id on auth.
          const placeholder = transitoryDevices[deviceId as Device['id']];
          if (placeholder) {
            const transitoryDevice = { transitoryKind: placeholder.transitoryKind };
            return {
              chipState: deriveChipState(undefined, transitoryDevice),
              missing: deriveMissingBuckets(undefined, transitoryDevice),
              deviceSnapshot: { deviceName: placeholder.name }
            };
          }
          // No room and no placeholder: broker race before device upsert; treat as transient.
          return undefined;
        }
        // Raw StatusDevice snapshot from the state-sync tree; mirrors `useChipState`.
        const proxy = locateProxy(deviceId as Device['id']);
        const proxyStatus = proxy?.state.status?.device[deviceId as Device['id']];
        // Read-only broker snapshot cast to the mutable param type; helpers only read.
        const statusForDerivation = proxyStatus as unknown as Parameters<typeof deriveChipState>[0];
        // Only `{agentStatus, modelId}` are threaded (the production chip's read
        // contract); the transitory/embedded branches stay for direct callers.
        // `heldConnectionBuckets` keeps this toast in step with the chip: an
        // unreachable camera's ~5s CONNECTION_FAILED<->CONNECTING cycle would
        // otherwise read as resolved on the CONNECTING half and clear the toast.
        // The hold releases on a genuine CONNECTED or when the bridge goes offline.
        const deviceForDerivation = {
          agentStatus: deviceRecord.agentStatus,
          modelId: deviceRecord.modelId,
          heldConnectionBuckets: connectionHolds[deviceId] ?? NO_CONNECTION_HOLD
        };
        // chipState gives the specific-fire verb; missing (full set) drives single-vs-combined grouping.
        const chipState = deriveChipState(statusForDerivation, deviceForDerivation);
        const missing = deriveMissingBuckets(statusForDerivation, deviceForDerivation);
        return {
          chipState,
          missing,
          deviceSnapshot: {
            // Fall back to the id until the room provider surfaces a display name.
            deviceName: deviceRecord.name ?? deviceId,
            agentStatus: deviceRecord.agentStatus,
            modelId: deviceRecord.modelId
          }
        };
      });

    // Classify each origin device: desired fire, confirmed resolved, or unknown.
    // Unknown (lookup undefined: broker race / mid-add) is held, never cleared,
    // so a transient miss doesn't flicker a live alert.
    const { fires, resolvedDeviceIds } = evaluateDesiredFires(origins, lookupChipState);
    const desiredByDevice = new Map<string, BucketFire>();
    for (const fire of fires) {
      desiredByDevice.set(fire.deviceId, fire);
    }
    const resolvedDevices = new Set(resolvedDeviceIds);

    // Posts the toast + emits fire-side analytics; reconciliation owns clear/tracking.
    const dispatchFire =
      overrides?.dispatchFire ??
      ((fire: BucketFire): void => {
        // Floating `<Notice>` toast carrying the chip verb as a clickable
        // deep-link — now the single Multi-Add surface (the bell-batch card
        // was removed). Per rule stay-in-scope: reuse `<Notice>`, no new component.
        const toast = buildBucketFireToastPayload(fire);
        // Message body accepts `string | ReactElement`; render the verb inline.
        // Use `<span role="button">` + a leading NBSP rather than `<button>`: an
        // inline-block button wraps atomically onto its own line when trailing
        // space is narrow (missing-model bucket), whereas an inline span glued by
        // the NBSP keeps the verb at the end of the line.
        // Open Advanced Settings on the bucket's sub-tab (same channel as the chip).
        const openSettingsForFire = (): void => {
          // LIVE depot, not this effect's `rooms` snapshot: the toast is
          // re-posted only when its desired alert identity changes, so this
          // closure outlives the render that made it. A room resolved from a
          // stale snapshot is one that may be gone — the same throw.
          const liveRooms = depot.getState().rooms;

          // Auth-required bulk-add cameras live in the transitory store,
          // never in `room.devices`. The room-walk below misses them and
          // the toast verb no-ops — same ENG-4406 miss as the tray gear.
          // Resolve first, synthesize (so `isVideoDevice` passes), and
          // deep-link Authentication: `defaultTab` is REQUIRED to arm
          // the ENG-4402 anti-eviction guard, same as `handleSettings`.
          // Per `stay-in-scope` (ai.knowledge/global).
          const placeholder = getTransitoryDevice(fire.deviceId as Device['id']);
          if (placeholder) {
            // Carry the room the user is in — the placeholder is in none.
            // Without it the call falls through to the inert hook-level
            // sentinel (see the binding comment above) and throws.
            const placeholderRoomId = resolveMountedRoomId(liveRooms, currentRoomId);
            if (placeholderRoomId === undefined) {
              // The toast outlived the room (the user navigated out
              // before clicking). No room ⇒ no settings dialog: stay a
              // silent no-op, same as the stale-device branch below.
              return;
            }
            const defaultTab = skipReasonToDefaultTab(toast.deepLinkBucket) ?? 'authentication';
            openSettings(toSyntheticDevice(placeholder), {
              defaultPanel: Panel.IrisConnection,
              defaultTab,
              roomId: placeholderRoomId
            });
            hide(toast.notificationId);
            return;
          }

          // Walk every room for the full Device record matching this
          // fire's deviceId. Same iteration pattern as the
          // `lookupChipState` closure above — the origin slice carries
          // no roomId, so we search. The OWNING room id falls out of the
          // same walk, and it is what the dialog must open against: the
          // hook-level binding is never a real room (see above).
          let deviceRecord: Device | undefined;
          let deviceRoomId: Room['id'] | undefined;
          for (const [roomId, room] of Object.entries(liveRooms)) {
            const candidate = (room.devices as Record<string, Device>)[fire.deviceId];
            if (candidate) {
              deviceRecord = candidate;
              deviceRoomId = roomId as Room['id'];
              break;
            }
          }
          if (!deviceRecord || deviceRoomId === undefined) {
            // Stale click — device removed between mount and click.
            return;
          }
          const defaultTab = skipReasonToDefaultTab(toast.deepLinkBucket);
          // Mirror `chipClickOptions`: include the deep-link only when
          // a concrete `defaultTab` exists (forward-compat). `roomId`
          // rides along unconditionally — it is not a deep-link hint but
          // the room the dialog resolves device status and auth against.
          openSettings(deviceRecord, {
            ...(defaultTab !== undefined && {
              defaultPanel: Panel.IrisConnection,
              defaultTab
            }),
            roomId: deviceRoomId
          });
          // Close + remove the floating toast — once the user has
          // clicked the verb and acted on the alert, the toast has
          // served its purpose. Same `hide()` call the close-X button
          // wires (see `panel.tsx`'s `onClose={hide}`), so behavior
          // and visuals match the manual-dismiss path. The bell-tray
          // entry stays intact (different slice — only the floating
          // toast is cleared).
          // Per `reference-rules-in-comments` (ai.knowledge/global).
          hide(toast.notificationId);
        };
        // Single `<div>` (one flex child); a bare Fragment would push the verb to its own row.
        const messageElement = createElement(
          'div',
          { className: 'text-sm' },
          // NBSP glues the trailing word to the verb so it never wraps alone.
          `${toast.message} `,
          createElement(
            'span',
            {
              'role': 'button',
              'tabIndex': 0,
              'className': 'cursor-pointer font-semibold underline',
              // Stable test selector for the per-bucket action element.
              'data-testid': `multi-add-toast-action-${fire.bucket}`,
              'onClick': openSettingsForFire,
              'onKeyDown': (event: React.KeyboardEvent<HTMLSpanElement>): void => {
                // Keyboard a11y for role="button": Enter/Space activate the deep-link.
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openSettingsForFire();
                }
              }
            },
            toast.actionLinkLabel
          )
        );
        post({
          // These notices carry a concrete settings/authentication action for the operator.
          actionable: true,
          id: toast.notificationId,
          deviceId: fire.deviceId,
          title: toast.title,
          message: messageElement,
          severity: toast.severity
        });

        // Fire-side analytics. Called only on a desired-identity change, so this
        // emits once per (device, chip_type) transition with no dedup slice.
        emitMultiAddAnalytics(EVENT_NAMES.MULTI_ADD_ALERT_SHOWN, {
          deviceId: fire.deviceId,
          chip_type: fireToChipType(fire)
        });
      });

    // Declarative reconciliation. Per origin device: desired fire → reconcile;
    // confirmed resolved → auto-clear; unknown → hold. Re-posting only on a
    // genuine identity change respects dismissal and avoids the bell-flicker.
    for (const deviceId of Object.keys(origins)) {
      const fire = desiredByDevice.get(deviceId);
      const lastId = lastDesired[deviceId] ?? null;

      if (fire) {
        // Desired specific/combined alert.
        const desiredId = buildBucketFireToastPayload(fire).notificationId;
        if (desiredId === lastId) {
          // No change — leave the (possibly user-dismissed) toast alone.
          continue;
        }
        // `clear()` (not `hide()`) deletes the entry from every surface at once
        // — the bell count follows devices, so a stale toast can't linger.
        if (lastId !== null) {
          clear(lastId);
        }
        dispatchFire(fire);
        setLastDesiredAlert(deviceId, desiredId);
        continue;
      }

      if (resolvedDevices.has(deviceId)) {
        // Confirmed resolved (lookup succeeded, nothing missing): auto-clear and forget.
        if (lastId !== null) {
          clear(lastId);
          clearLastDesiredAlert(deviceId);
        }
        continue;
      }

      // Unknown (lookup undefined): hold — leaving the alert stops the flicker
      // while a camera is mid-add or between the room-walk and transitory store.
    }

    // Orphan cleanup: a device gone from the origin slice (e.g. migrated on
    // auth-commit) still has a tracked identity — clear its toast and forget it.
    for (const deviceId of Object.keys(lastDesired)) {
      if (deviceId in origins) continue;
      clear(lastDesired[deviceId]!);
      clearLastDesiredAlert(deviceId);
    }

    // Adopt the connection-failure hold here for every origin, not only from
    // `useChipState` (whose writer unmounts with the tile) — otherwise the
    // "Connect video" toast blinked off on each CONNECTING leg of the encoder's
    // ~5s retry cycle. `noteConnectionObservation` is idempotent, so this and the
    // per-tile writer converge without churn.
    const holdObservations = Object.keys(origins)
      .map((deviceId) => {
        let record: { agentStatus?: T_Maybe<Agent['status']> } | undefined;
        for (const room of Object.values(rooms)) {
          const candidate = (
            room.devices as Record<string, { agentStatus?: T_Maybe<Agent['status']> }>
          )[deviceId];
          if (candidate) {
            record = candidate;
            break;
          }
        }
        // No room record (placeholder/broker race): auth-only, no connection verdict to adopt.
        if (!record) return undefined;
        const proxy = locateProxy(deviceId as Device['id']);
        const proxyStatus = proxy?.state.status?.device[deviceId as Device['id']];
        const aspects = proxyStatus as unknown as
          | {
              video?: { connectionStatus?: { status?: number } };
              movement?: { connectionStatus?: { status?: number } };
            }
          | undefined;
        return {
          deviceId,
          observation: {
            // Fresh missing (no held buckets); keeps only connection buckets.
            missing: deriveMissingBuckets(
              proxyStatus as unknown as Parameters<typeof deriveMissingBuckets>[0],
              { agentStatus: record.agentStatus }
            ),
            // Release contract matches useChipState: video only on CONNECTED(3); control loose.
            videoResolved: isVideoAspectResolved(aspects?.video?.connectionStatus?.status),
            controlResolved: isAspectResolved(aspects?.movement?.connectionStatus?.status),
            present: proxyStatus !== undefined && record.agentStatus === 'online'
          }
        };
      })
      .filter((o): o is NonNullable<typeof o> => o !== undefined);

    // Orphan sweep: release the hold for a device genuinely gone from the room
    // so a stale verdict can't leak into a re-added device's connect window.
    // The criterion is "absent from every room's device list AND not a live
    // origin" — keying on origin membership alone flapped the "Connect video"
    // chip/toast for a persisted failing camera still held by the per-tile writer.
    const deviceIdsInRoom = new Set<string>();
    for (const room of Object.values(rooms)) {
      for (const id of Object.keys((room.devices ?? {}) as Record<string, unknown>)) {
        deviceIdsInRoom.add(id);
      }
    }
    const originIds = new Set(Object.keys(origins));
    const heldDeviceIds = Object.keys(depot.getState().connectionFailureHoldByDevice);
    const orphanedHolds = heldDeviceIds.filter((deviceId) =>
      isConnectionHoldOrphaned(deviceId, deviceIdsInRoom, originIds)
    );
    if (holdObservations.length > 0 || orphanedHolds.length > 0) {
      depot.getState().produce((draft) => {
        holdObservations.forEach(({ deviceId, observation }) =>
          noteConnectionObservation(draft, deviceId as Device['id'], observation)
        );
        orphanedHolds.forEach((deviceId) => releaseConnectionHold(draft, deviceId));
      });
    }
    // Deps are store object refs: ref-equality re-runs the effect on any tick
    // touching origins, chip state, placeholders, or the tracked identity.
  }, [
    openSettings,
    // Read by the click path; constant only while this hook is hosted above
    // every room provider.
    currentRoomId,
    origins,
    agents,
    rooms,
    transitoryDevices,
    lastDesired,
    overrides,
    connectionHolds
  ]);
};
